import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "@/lib/toast";
import { api, formatApiErrorDetail, youtubeEmbed } from "@/lib/api";
import { useLive } from "@/context/LiveContext";
import CitySelect from "@/components/CitySelect";
import { CATEGORIES } from "@/lib/categories";
import { phoneError, emailError, cleanPhoneInput, collect, prettyPhone } from "@/lib/validate";
import { Play } from "lucide-react";

const EMPTY = { name: "", phone: "", email: "", message: "", event_date: "", pax: "", city: "", event_type: "wedding", business_type: "banquet_hall", intent: "plan" };
const EVENT_TYPES = ["wedding", "reception", "engagement", "sangeet", "mehendi", "haldi", "anniversary", "birthday party", "corporate event", "naming ceremony"];
const INTENTS = [["plan", "Planning an event"], ["list", "Listing my business"], ["other", "Something else"]];
const FAQS = [
  ["Do families pay anything?", "No. Searching, shortlisting and our help on the phone are free for families. You pay the venue or vendor directly."],
  ["Do you take a commission from businesses?", "No commission on any booking. Businesses pay a fixed plan for their category — the rates are on the Pricing page."],
  ["Which cities do you cover?", "We started in Belgaum and now list venues and vendors across Karnataka, Maharashtra and Goa, with more cities every month."],
];
const today = () => new Date().toISOString().slice(0, 10);

function ShortsFrame({ url }) {
  const embed = youtubeEmbed(url);
  return (
    <figure className="mx-auto w-[240px] sm:w-[260px]" data-testid="intro-video">
      <div className="relative rounded-[38px] bg-[#1a0a0b] p-[9px] shadow-[0_30px_70px_rgba(42,15,17,0.30)]">
        <div className="absolute left-1/2 -translate-x-1/2 top-[16px] w-20 h-5 rounded-full bg-black z-10" aria-hidden/>
        <div className="relative rounded-[30px] overflow-hidden bg-[color:var(--maroon-2)] aspect-[9/16]">
          {embed ? (
            <iframe src={`${embed}?rel=0&modestbranding=1&playsinline=1`} title="What is DartMyVenue" className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen/>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 rings">
              <img src="/logo-light.png" alt="DartMyVenue" className="w-36"/>
              <span className="mt-7 w-12 h-12 rounded-full bg-[color:var(--gold-2)] text-[color:var(--maroon-2)] flex items-center justify-center"><Play className="w-5 h-5 ml-0.5"/></span>
            </div>
          )}
        </div>
      </div>
      <figcaption className="text-center mt-3 text-[14px] text-[color:var(--ink-2)]">DartMyVenue in under a minute</figcaption>
    </figure>
  );
}

/** Contact: a note to the team written as one sentence, with the direct lines beside it. */
export default function Contact() {
  const { site } = useLive();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [errs, setErrs] = useState({});
  const set = (k) => (e) => { const v = e?.target ? e.target.value : e; setF((cur) => ({ ...cur, [k]: v })); setErrs((x) => (x[k] ? { ...x, [k]: "" } : x)); };

  const phone = site.phone || "+91 74112 98777";
  const tel = phone.replace(/[^\d+]/g, "");
  const waNumber = (site.whatsapp || "917411298777").replace(/\D/g, "");
  const wa = `https://wa.me/${waNumber}?text=${encodeURIComponent("Namaste DartMyVenue, I'd like help planning my event.")}`;

  const submit = async (e) => {
    e.preventDefault();
    const found = collect({ name: f.name.trim().length >= 2 ? "" : "Add your name", phone: phoneError(f.phone, { required: true }), email: emailError(f.email) });
    setErrs(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    try {
      const plan = f.intent === "plan";
      await api.post("/contact", {
        name: f.name.trim(), phone: f.phone, email: f.email || null, intent: f.intent, message: f.message.trim(),
        city: f.intent === "other" ? null : f.city || null,
        event_type: plan ? f.event_type : null, event_date: plan ? f.event_date || null : null, pax: plan && f.pax ? Number(f.pax) : null,
        business_type: f.intent === "list" ? f.business_type : null,
      });
      setSent({ name: f.name.trim().split(" ")[0], phone: prettyPhone(f.phone) });
      setF((cur) => ({ ...EMPTY, intent: cur.intent }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't send. Call or WhatsApp us instead.");
    } finally { setBusy(false); }
  };

  const errList = Object.values(errs).filter(Boolean);
  const nameField = <input value={f.name} onChange={set("name")} placeholder="your name" autoComplete="name" aria-label="Your name" aria-invalid={!!errs.name} className={`inline-field w-[7.5em] ${errs.name ? "invalid" : ""}`} data-testid="contact-name"/>;
  const phoneField = <input type="tel" inputMode="tel" value={f.phone} onChange={(e) => set("phone")(cleanPhoneInput(e.target.value))} onBlur={() => f.phone && setErrs((x) => ({ ...x, phone: phoneError(f.phone) }))} placeholder="98765 43210" autoComplete="tel" aria-label="Mobile number" aria-invalid={!!errs.phone} className={`inline-field w-[7.2em] ${errs.phone ? "invalid" : ""}`} data-testid="contact-phone"/>;
  const cityField = <CitySelect value={f.city} onChange={(c) => set("city")(c)} placeholder="your city" variant="inline" testId="contact-city"/>;

  return (
    <div className="contact-page -mb-24">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 pt-12 md:pt-16">
        <h1 className="font-display text-[38px] sm:text-[48px] md:text-[56px] leading-[1.05] text-[color:var(--maroon)]">Talk to the DartMyVenue team</h1>
        <p className="mt-4 max-w-[60ch] text-[17px] leading-relaxed text-[color:var(--ink-2)]">
          We're a small team in Belgaum. Tell us what you're planning and we'll call you back the same day with venues and vendors that are free on your date.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 pt-9 pb-14 grid lg:grid-cols-[minmax(0,1fr)_300px] gap-10 lg:gap-14 items-start">
        {/* The note */}
        <div className="note-card" data-testid="contact-card">
          <AnimatePresence mode="wait" initial={false}>
            {sent ? (
              <motion.div key="ok" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="py-6 sm:py-10" data-testid="contact-success" role="status">
                <p className="font-display text-[30px] sm:text-[38px] leading-tight text-[color:var(--maroon)]">Thank you{sent.name ? `, ${sent.name}` : ""}.</p>
                <p className="mt-3 text-[18px] leading-relaxed text-[color:var(--ink-2)] max-w-[46ch]">Your note is with the team. We'll call you on <b className="text-[color:var(--ink)] font-semibold whitespace-nowrap">{sent.phone}</b> between 9 AM and 9 PM — usually within a few hours.</p>
                <button onClick={() => setSent(null)} className="mt-8 text-[15px] font-semibold text-[color:var(--maroon)] underline underline-offset-4 decoration-[color:var(--gold)]">Write another note</button>
              </motion.div>
            ) : (
              <motion.form key="form" noValidate onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="contact-form">
                <div className="flex gap-x-5 sm:gap-x-6 overflow-x-auto no-scrollbar border-b border-[color:var(--line)] pb-3 -mx-1 px-1" role="tablist" aria-label="What is this about?">
                  {INTENTS.map(([k, l]) => (
                    <button type="button" key={k} role="tab" aria-selected={f.intent === k} onClick={() => setF((x) => ({ ...x, intent: k }))}
                      className={`relative shrink-0 whitespace-nowrap pb-1 text-[14px] sm:text-[15px] font-semibold transition-colors ${f.intent === k ? "text-[color:var(--maroon)]" : "text-neutral-500 hover:text-[color:var(--maroon)]"}`} data-testid={`intent-${k}`}>
                      {l}
                      {f.intent === k && <motion.span layoutId="intent-line" className="absolute left-0 right-0 -bottom-[13px] h-[3px] rounded-full bg-[color:var(--gold)]"/>}
                    </button>
                  ))}
                </div>

                <div className="sentence mt-7" data-testid="contact-sentence">
                  {f.intent === "plan" && (
                    <p>
                      Namaste, I'm {nameField}. We're planning a{" "}
                      <select value={f.event_type} onChange={set("event_type")} aria-label="Event" className="inline-field inline-select" style={{ width: `calc(${f.event_type.length}ch + 1.5em)` }} data-testid="contact-event-type">{EVENT_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}</select>{" "}
                      in {cityField} on{" "}
                      <input type="date" min={today()} value={f.event_date} onChange={set("event_date")} aria-label="Event date" className={`inline-field inline-date ${f.event_date ? "" : "is-empty"}`} data-testid="contact-date"/>{" "}
                      for about{" "}
                      <input type="number" inputMode="numeric" min="1" value={f.pax} onChange={set("pax")} placeholder="300" aria-label="Number of guests" className="inline-field w-[3.6em] text-center" data-testid="contact-pax"/>{" "}
                      guests. You can reach me on {phoneField}.
                    </p>
                  )}
                  {f.intent === "list" && (
                    <p>
                      Namaste, I'm {nameField}. I run a{" "}
                      <select value={f.business_type} onChange={set("business_type")} aria-label="Type of business" className="inline-field inline-select" style={{ width: `calc(${(CATEGORIES.find((c) => c.key === f.business_type)?.label || "").length}ch + 1.5em)` }} data-testid="contact-business">{CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label.toLowerCase()}</option>)}</select>{" "}
                      business in {cityField} and would like to list it on DartMyVenue. You can reach me on {phoneField}.
                    </p>
                  )}
                  {f.intent === "other" && (
                    <p>Namaste, I'm {nameField}. You can reach me on {phoneField}.</p>
                  )}
                </div>

                {errList.length > 0 && (
                  <ul className="mt-4 space-y-1" role="alert">
                    {errList.map((m) => <li key={m} className="field-error !mt-0 text-[14px]">{m}</li>)}
                  </ul>
                )}

                <div className="mt-8 grid sm:grid-cols-[1fr_1.4fr] gap-4">
                  <label className="block">
                    <span className="label">Email <span className="font-normal text-neutral-500">(optional)</span></span>
                    <input type="email" inputMode="email" value={f.email} onChange={set("email")} onBlur={() => f.email && setErrs((x) => ({ ...x, email: emailError(f.email) }))} className={`input ${errs.email ? "invalid" : ""}`} placeholder="you@example.com" autoComplete="email" data-testid="contact-email"/>
                  </label>
                  <label className="block">
                    <span className="label">{f.intent === "other" ? "How can we help?" : <>Anything else <span className="font-normal text-neutral-500">(optional)</span></>}</span>
                    <textarea rows={f.intent === "other" ? 4 : 2} value={f.message} onChange={set("message")} className="input resize-y" placeholder={f.intent === "list" ? "Capacity, rooms, packages, how long you've been running…" : f.intent === "other" ? "Partnerships, press, feedback — anything." : "Must-haves, style, timings…"} data-testid="contact-message"/>
                  </label>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                  <button disabled={busy} className="btn-wine !px-8 !py-3.5 text-[16px]" data-testid="contact-submit">{busy ? "Sending…" : "Send to the team"}</button>
                  <p className="text-[14px] text-[color:var(--ink-2)]">We call back between 9 AM and 9 PM, usually within a few hours.</p>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>

        {/* Direct lines */}
        <aside className="lg:pt-2" aria-label="Contact details">
          <dl className="direct-lines">
            <div>
              <dt>Call</dt>
              <dd><a href={`tel:${tel}`} className="font-num text-[26px] text-[color:var(--maroon)] hover:text-[color:var(--maroon-3)]" data-testid="cta-call">{phone}</a></dd>
            </div>
            <div>
              <dt>WhatsApp</dt>
              <dd><a href={wa} target="_blank" rel="noreferrer" className="link-strong" data-testid="cta-whatsapp">Message us on WhatsApp</a><span className="block text-[14px] text-[color:var(--ink-2)] mt-0.5">Replies within minutes in office hours</span></dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd><a href={`mailto:${site.email || "hello@dartmyvenue.com"}`} className="link-strong break-all" data-testid="cta-email">{site.email || "hello@dartmyvenue.com"}</a></dd>
            </div>
            <div>
              <dt>Office</dt>
              <dd className="text-[16px] text-[color:var(--ink)] leading-relaxed">{site.address || "Belgaum, Karnataka, India"}<span className="block text-[14px] text-[color:var(--ink-2)]">{site.hours || "9 AM – 9 PM, every day"}</span></dd>
            </div>
          </dl>
          <p className="mt-8 text-[15px] text-[color:var(--ink-2)] leading-relaxed">Run a venue, kitchen or studio? <Link to="/pricing" className="link-strong">See the plans</Link> or <Link to="/register?role=venue_owner" className="link-strong">list your business</Link>.</p>
        </aside>
      </section>

      {/* After you write */}
      <section className="border-t border-[color:var(--line)] paper">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 pt-14 md:pt-20 pb-24 md:pb-32 grid lg:grid-cols-[minmax(0,1fr)_300px] gap-12 lg:gap-14 items-start">
          <div>
            <h2 className="font-display text-[30px] md:text-[38px] leading-tight text-[color:var(--maroon)]">What happens after you write</h2>
            <ol className="steps mt-8">
              <li><b>Same day.</b> Someone from our team calls to understand the event, the budget and what matters most to you.</li>
              <li><b>Next day.</b> You get a short list on WhatsApp: places and vendors that are actually free on your date.</li>
              <li><b>That week.</b> We set up site visits and tastings for the ones you like. You book directly with them.</li>
            </ol>

            <h2 className="font-display text-[26px] md:text-[30px] leading-tight text-[color:var(--maroon)] mt-14">Questions people ask first</h2>
            <div className="mt-5 divide-y divide-[color:var(--line)] border-y border-[color:var(--line)]">
              {FAQS.map(([q, a]) => (
                <details key={q} className="group py-4">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[17px] font-semibold text-[color:var(--ink)]">
                    {q}<span className="text-[color:var(--gold)] text-[22px] leading-none transition-transform group-open:rotate-45" aria-hidden>+</span>
                  </summary>
                  <p className="mt-2 text-[15px] leading-relaxed text-[color:var(--ink-2)] max-w-[62ch]">{a}</p>
                </details>
              ))}
            </div>
          </div>
          <ShortsFrame url={site.intro_video_url}/>
        </div>
      </section>
    </div>
  );
}
